import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAuthService } from "../../services/factory";
import { requestManager } from "../../utils/requestManager";
import { VerificationMethod, User } from "../../services/authService";
import useOtpTimer from "../../hooks/useOtpTimer";
import { isSkipPhoneVerification } from "../../utils/metrika";
import { AuthView } from "./AuthViewRouter";
import Button from "../ui/Button";
import Input from "../ui/Input";
import FormGroup from "../ui/FormGroup";

interface PhoneVerificationViewProps {
  phone: string;
  onSwitchView: (view: AuthView) => void;
  onVerify: (code: string) => Promise<void>;
  onMobileIdSuccess?: (user?: User, isNewUser?: boolean) => void;
}

const PhoneVerificationView: React.FC<PhoneVerificationViewProps> = ({
  phone,
  onSwitchView,
  onVerify,
  onMobileIdSuccess,
}) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationMethod, setVerificationMethod] =
    useState<VerificationMethod>("mobile_id");
  const [sent, setSent] = useState(false);
  const [smsSentCount, setSmsSentCount] = useState(0);
  const [mobileIdReqId, setMobileIdReqId] = useState<string | null>(null);
  const sentRef = useRef(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const { timeLeft, startTimer, resetTimer, formatTime } = useOtpTimer();

  const getSmsFallbackTimeout = (): number => {
    if (
      typeof window !== "undefined" &&
      (window as any).SkAuthConfig?.smsFallbackTimeoutSeconds
    ) {
      const val = parseInt(
        (window as any).SkAuthConfig.smsFallbackTimeoutSeconds,
        10,
      );
      if (!isNaN(val) && val > 0) {
        return val;
      }
    }
    return parseInt(import.meta.env.VITE_SMS_FALLBACK_TIMEOUT || "60", 10);
  };
  const SMS_FALLBACK_TIMEOUT = getSmsFallbackTimeout();

  const getOtpSmsLength = (): number => {
    if (
      typeof window !== "undefined" &&
      (window as any).SkAuthConfig?.otpSmsLength
    ) {
      const val = parseInt((window as any).SkAuthConfig.otpSmsLength, 10);
      if (!isNaN(val) && val >= 4 && val <= 10) {
        return val;
      }
    }
    return parseInt(import.meta.env.VITE_OTP_SMS_LENGTH || "6", 10);
  };
  const OTP_SMS_LENGTH = getOtpSmsLength();

  const isFlashcallEnabled = (): boolean => {
    if (
      typeof window !== "undefined" &&
      (window as any).SkAuthConfig?.flashcallEnabled !== undefined
    ) {
      return !!(window as any).SkAuthConfig.flashcallEnabled;
    }
    return true; // Default fallback to true if config not available
  };
  const FLASHCALL_ENABLED = isFlashcallEnabled();

  const authService = getAuthService();

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const sendVerification = async (
    method: VerificationMethod,
    force = false,
  ) => {
    if (isSkipPhoneVerification()) {
      onVerify("000000");
      return;
    }

    const key = `sendVerification:${phone}:${method}`;
    if (!force && requestManager.isRequestInProgress(key)) {
      return;
    }
    if (method === "sms" && smsSentCount >= 4) {
      setError("Превышено максимальное количество SMS (4)");
      return;
    }

    stopPolling();
    setLoading(true);
    setError(null);
    try {
      if (force) {
        requestManager.cancelRequest(key);
      }
      const result = await authService.sendVerification(phone, {
        method,
        force,
      });
      if (result.success) {
        setSent(true);
        setError(null);
        sentRef.current = true;
        setVerificationMethod(method);

        if (method === "mobile_id" && result.authReqId) {
          setMobileIdReqId(result.authReqId);
          startTimer(15); // 15 секунд ожидания Мобильного ID перед предложением звонка-сброса
        } else {
          if (method === "sms") {
            setSmsSentCount((prev) => prev + 1);
          }
          startTimer(SMS_FALLBACK_TIMEOUT);
        }
      } else {
        if (method === "mobile_id") {
          const fallbackMethod = FLASHCALL_ENABLED ? "flashcall" : "sms";
          console.log(
            `Mobile ID unavailable, falling back to ${fallbackMethod}`,
          );
          await sendVerification(fallbackMethod, true);
          return;
        }
        setError(
          result.errors?.[0] ||
            `Ошибка отправки ${method === "flashcall" ? "звонка" : "SMS"}`,
        );
      }
    } catch (err) {
      if (method === "mobile_id") {
        const fallbackMethod = FLASHCALL_ENABLED ? "flashcall" : "sms";
        await sendVerification(fallbackMethod, true);
        return;
      }
      console.error(`Error sending ${method} verification:`, err);
      setError("Ошибка сети");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSkipPhoneVerification()) {
      onVerify("000000");
      return;
    }

    const key = `sendVerification:${phone}:mobile_id`;

    if (!sentRef.current && !requestManager.isRequestInProgress(key)) {
      sendVerification("mobile_id");
      sentRef.current = true;
    }

    return () => {
      stopPolling();
      resetTimer();
    };
  }, [phone, stopPolling, resetTimer, onVerify]);

  // Mobile ID polling logic
  useEffect(() => {
    if (verificationMethod === "mobile_id" && mobileIdReqId && sent) {
      pollingRef.current = setInterval(async () => {
        try {
          const res = await authService.checkMobileIdStatus(mobileIdReqId);
          if (res.success && res.status === "success") {
            stopPolling();
            if (onMobileIdSuccess) {
              onMobileIdSuccess(res.user, res.isNewUser);
            }
          } else if (res.status === "awaiting_sms_otp") {
            setVerificationMethod("sms");
            setError(null);
            startTimer(SMS_FALLBACK_TIMEOUT);
          } else if (res.status === "failed" || res.status === "expired") {
            stopPolling();
            const fallbackMethod = FLASHCALL_ENABLED ? "flashcall" : "sms";
            sendVerification(fallbackMethod, true);
          }
        } catch {
          // Ignore transient polling errors
        }
      }, 2000);
    }

    return () => {
      stopPolling();
    };
  }, [
    verificationMethod,
    mobileIdReqId,
    sent,
    authService,
    onMobileIdSuccess,
    stopPolling,
    SMS_FALLBACK_TIMEOUT,
    startTimer,
    FLASHCALL_ENABLED,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedCode = code.trim();

    if (!trimmedCode) {
      setError(
        verificationMethod === "flashcall"
          ? "Введите последние 4 цифры номера"
          : "Введите код из SMS",
      );
      return;
    }

    if (verificationMethod === "flashcall" && trimmedCode.length !== 4) {
      setError("Введите 4 цифры номера");
      return;
    }

    stopPolling();
    setLoading(true);
    setError(null);
    try {
      await onVerify(trimmedCode);
    } catch (err) {
      setError("Ошибка проверки кода");
      console.error("Verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToFlashcall = () => {
    stopPolling();
    sendVerification("flashcall", true);
  };

  const handleSwitchToSms = async () => {
    stopPolling();
    await sendVerification("sms", true);
  };

  const handleChangePhone = () => {
    stopPolling();
    setSmsSentCount(0);
    onSwitchView("identifier");
  };

  const exampleBadge = phone.slice(-4);
  const smsRemaining = 4 - smsSentCount;
  const showFloatingLabel = code.trim().length > 0 || !!error;

  return (
    <div className="auth-view">
      <p className="auth-verify-text">
        {verificationMethod === "mobile_id" ? (
          <>Подтвердите вход на вашем телефоне (Мобильный ID)</>
        ) : verificationMethod === "flashcall" ? (
          <>
            Введите последние 4 цифры входящего номера
            <br />
            или код из SMS
          </>
        ) : (
          <>Введите {OTP_SMS_LENGTH}-значный код из SMS</>
        )}
      </p>

      {verificationMethod === "mobile_id" && (
        <div className="auth-mobile-id-banner">
          <div className="auth-mobile-id-spinner" />
          <div className="auth-mobile-id-text">
            <p className="auth-mobile-id-title">
              Ожидаем подтверждения на телефоне
            </p>
            <p className="auth-mobile-id-subtitle">
              Нажмите «Принять» во всплывающем окне на вашем смартфоне
            </p>
          </div>
        </div>
      )}

      {verificationMethod === "flashcall" && (
        <div className="auth-example">
          <span className="auth-example">Например: +X XXX XXX </span>
          <span className="auth-example-numbers">{exampleBadge}</span>
        </div>
      )}

      <div className="auth-verification-data">
        <div>
          <span className="auth-verification-text">
            {verificationMethod === "mobile_id"
              ? "Запрос отправлен на "
              : verificationMethod === "flashcall"
                ? "Звоним на "
                : "Отправляем SMS на "}
          </span>
          <span className="auth-verification-num">{phone}</span>
        </div>
        <button
          className="auth-change-num"
          onClick={handleChangePhone}
          disabled={loading}
        >
          Изменить
        </button>
      </div>

      {verificationMethod !== "mobile_id" && (
        <form
          onSubmit={handleSubmit}
          className="tw-flex tw-flex-col gatw-p-4"
          noValidate
        >
          <FormGroup error={error || undefined}>
            <div className={`auth-field ${error ? "is-error" : ""}`}>
              {showFloatingLabel && (
                <label
                  htmlFor="verification-code"
                  className="auth-field__label"
                >
                  {verificationMethod === "flashcall"
                    ? "Последние 4 цифры"
                    : "Код из SMS"}
                </label>
              )}
              <Input
                id="verification-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(null);
                }}
                placeholder={
                  verificationMethod === "flashcall"
                    ? "Последние 4 цифры"
                    : "Код из SMS"
                }
                className="tw-w-full"
                autoFocus
                disabled={loading}
                maxLength={
                  verificationMethod === "flashcall" ? 4 : OTP_SMS_LENGTH
                }
                clearable
                onClear={() => {
                  setCode("");
                  setError(null);
                }}
                error={!!error}
              />
            </div>
          </FormGroup>

          <Button
            type="submit"
            variant="primary"
            className="tw-w-full"
            disabled={!code.trim() || loading}
            loading={loading}
          >
            {loading ? "Проверка..." : "Войти"}
          </Button>
        </form>
      )}

      {/* Fallback timer & button chain: Mobile ID -> (Flash-Call if enabled) -> SMS */}
      {verificationMethod === "mobile_id" ? (
        FLASHCALL_ENABLED ? (
          timeLeft > 0 ? (
            <p className="auth-recive-sms-button">
              Запросить звонок-сброс через {formatTime(timeLeft)}
            </p>
          ) : (
            <div className="auth-recive-sms-timeout-box">
              <button
                type="button"
                className="auth-recive-sms-timeout"
                onClick={handleSwitchToFlashcall}
                disabled={loading}
              >
                Получить звонок-сброс
              </button>
            </div>
          )
        ) : timeLeft > 0 ? (
          <p className="auth-recive-sms-button">
            Получить код по SMS через {formatTime(timeLeft)}
          </p>
        ) : (
          <div className="auth-recive-sms-timeout-box">
            <button
              type="button"
              className={
                "auth-recive-sms-timeout " +
                (loading || smsRemaining <= 0 ? "disabled" : "")
              }
              onClick={handleSwitchToSms}
              disabled={loading || smsRemaining <= 0}
            >
              Получить код по SMS
            </button>
          </div>
        )
      ) : verificationMethod === "flashcall" ? (
        timeLeft > 0 ? (
          <p className="auth-recive-sms-button">
            Получить код по SMS через {formatTime(timeLeft)}
          </p>
        ) : (
          <div className="auth-recive-sms-timeout-box">
            <button
              type="button"
              className={
                "auth-recive-sms-timeout " +
                (loading || smsRemaining <= 0 ? "disabled" : "")
              }
              onClick={handleSwitchToSms}
              disabled={loading || smsRemaining <= 0}
            >
              Получить код по SMS
            </button>
          </div>
        )
      ) : timeLeft > 0 ? (
        <p className="auth-recive-sms-button">
          Отправить SMS повторно через {formatTime(timeLeft)}
        </p>
      ) : (
        <div className="auth-recive-sms-timeout-box">
          <button
            type="button"
            className={
              "auth-recive-sms-timeout " +
              (loading || smsRemaining <= 0 ? "disabled" : "")
            }
            onClick={handleSwitchToSms}
            disabled={loading || smsRemaining <= 0}
          >
            Отправить SMS повторно
          </button>
        </div>
      )}
    </div>
  );
};

export default PhoneVerificationView;
